import React, { useRef } from "react";
import type { ImageFieldValue } from "@apa/core-renderers";
import type { ContentTypeField, ContentTypeFieldCondition } from "../API/Models";
import ImagePickerInput from "./ImagePickerInput";
import FilePickerInput from "./FilePickerInput";
import EmbeddedComponentsEditor from "./PageEditor/EmbeddedComponentsEditor";
import { resolveStorageUrl } from "../Helper/storage";
import { FiCalendar } from "react-icons/fi";

export const normalizeFieldKey = (value?: string | null) =>
	(value ?? "")
		.trim()
		.replace(/[^a-zA-Z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

export const normalizeFieldKeyLookup = (value?: string | null) => normalizeFieldKey(value).toLowerCase();

const normalizeFieldType = (value?: string | null) => (value ?? "").trim().toLowerCase();

const getNumberValue = (raw: any): number | null => {
	if (raw == null || raw === "") return null;
	const parsed = Number(raw);
	return Number.isFinite(parsed) ? parsed : null;
};

const isEmptyValue = (value: any) => {
	if (value == null) return true;
	if (typeof value === "string") return value.trim().length === 0;
	if (Array.isArray(value)) return value.length === 0;
	if (typeof value === "object") return Object.keys(value).length === 0;
	return false;
};

const resolveImagePreview = (value?: ImageFieldValue | null) => {
	if (!value) return { src: "", center: null };
	if (typeof value === "string") return { src: value, center: null };
	return { src: value.src ?? "", center: value.options?.center ?? null };
};

const clampNumber = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const getCenterPercent = (center?: { enabled?: boolean; x?: number; y?: number } | null) => {
	if (!center?.enabled) return null;
	const x = typeof center.x === "number" && Number.isFinite(center.x) ? center.x : 0;
	const y = typeof center.y === "number" && Number.isFinite(center.y) ? center.y : 0;
	return {
		x: (clampNumber(x, -1, 1) + 1) * 50,
		y: (clampNumber(y, -1, 1) + 1) * 50
	};
};

const toDisplayDate = (value: any) => {
	if (value == null) return "";
	const raw = String(value).trim();
	if (!raw) return "";
	if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
	if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) return raw.replace(/-/g, "/");
	if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
		const datePart = raw.slice(0, 10);
		const [year, month, day] = datePart.split("-");
		return `${day}/${month}/${year}`;
	}
	return raw;
};

const normalizeDateInput = (value: string) => {
	const raw = value.trim();
	if (!raw) return "";
	if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
	if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) return raw.replace(/-/g, "/");
	if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
		const [year, month, day] = raw.split("-");
		return `${day}/${month}/${year}`;
	}
	return raw;
};

const toPickerDate = (value: any) => {
	const raw = toDisplayDate(value);
	if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
		const [day, month, year] = raw.split("/");
		return `${year}-${month}-${day}`;
	}
	if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
	return "";
};

const buildTodayValue = () => {
	const now = new Date();
	const day = String(now.getDate()).padStart(2, "0");
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const year = String(now.getFullYear());
	return `${day}/${month}/${year}`;
};

const resolveFieldKey = (field: ContentTypeField, index: number) =>
	normalizeFieldKey(field.key ?? field.title ?? `field-${index}`);

export const hasAnyFieldValue = (fields: ContentTypeField[] = [], values: Record<string, any>) => {
	for (let index = 0; index < fields.length; index++) {
		const field = fields[index];
		const key = resolveFieldKey(field, index);
		if (!key) continue;
		if (!isEmptyValue(values?.[key])) return true;
	}
	return false;
};

export const validateContentItemValues = (fields: ContentTypeField[] = [], values: Record<string, any>) => {
	const errors: Record<string, string> = {};
	const keyCounts = new Map<string, number>();
	fields.forEach((field) => {
		const keyLookup = normalizeFieldKeyLookup(field.key ?? field.title ?? "");
		if (!keyLookup) return;
		keyCounts.set(keyLookup, (keyCounts.get(keyLookup) ?? 0) + 1);
	});
	fields.forEach((field, index) => {
		const key = resolveFieldKey(field, index);
		const keyLookup = normalizeFieldKeyLookup(field.key ?? field.title ?? "");
		if (!key) return;
		if (keyLookup && keyCounts.get(keyLookup) && keyCounts.get(keyLookup)! > 1) {
			errors[key] = "Duplicate field key.";
		}
		const condition: ContentTypeFieldCondition | null | undefined = field.condition;
		const required = condition?.required ?? false;
		const type = normalizeFieldType(field.type);
		const value = values?.[key];

		if (required && isEmptyValue(value)) {
			errors[key] = "This field is required.";
			return;
		}

		if (type === "text") {
			const length = condition?.length;
			const textValue = typeof value === "string" ? value : "";
			if (length?.minimum != null && textValue.length < length.minimum) {
				errors[key] = `Minimum length is ${length.minimum}.`;
			}
			if (length?.maximum != null && textValue.length > length.maximum) {
				errors[key] = `Maximum length is ${length.maximum}.`;
			}
		}

		if (type === "number" || type === "int" || type === "float") {
			const numberValue = getNumberValue(value);
			const range = condition?.number;
			if (numberValue != null && range?.minimum != null && numberValue < range.minimum) {
				errors[key] = `Minimum value is ${range.minimum}.`;
			}
			if (numberValue != null && range?.maximum != null && numberValue > range.maximum) {
				errors[key] = `Maximum value is ${range.maximum}.`;
			}
		}
	});

	return { errors, hasErrors: Object.keys(errors).length > 0 };
};

type ContentItemFormProps = {
	fields: ContentTypeField[];
	value: Record<string, any>;
	onChange: (next: Record<string, any>) => void;
	disabled?: boolean;
	errors?: Record<string, string>;
};

/** Dynamic input form for content fields based on the content type schema. */
const ContentItemForm: React.FC<ContentItemFormProps> = ({ fields, value, onChange, disabled = false, errors = {} }) => {
	const dateInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
	if (!fields || fields.length === 0) {
		return <p className="text-sm text-slate-600">No fields are defined for this content type.</p>;
	}

	return (
		<div className="space-y-4">
			{fields.map((field, index) => {
				const label = field.title?.trim() || `Field ${index + 1}`;
				const key = resolveFieldKey(field, index);
				const type = normalizeFieldType(field.type);
				const error = errors?.[key];
				const fieldValue = value?.[key];

				if (!key) {
					return (
						<div key={`field-${index}`} className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
							Field title is required.
						</div>
					);
				}

				if (type === "boolean") {
					return (
						<label key={key} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
							<input
								type="checkbox"
								checked={Boolean(fieldValue)}
								onChange={(event) => onChange({ ...value, [key]: event.target.checked })}
								disabled={disabled}
							/>
							<span>{label}</span>
						</label>
					);
				}

				if (type === "image") {
					const preview = resolveImagePreview(fieldValue as ImageFieldValue | null);
					const previewSrc = preview.src ? resolveStorageUrl(preview.src) : "";
					const center = getCenterPercent(preview.center);
					const centerPosition = center ? `${center.x}% ${center.y}%` : undefined;
					return (
						<div key={key} className="flex flex-wrap items-start gap-3">
							<div className="min-w-[240px] flex-1 space-y-1">
								<ImagePickerInput
									label={label}
									value={fieldValue ?? ""}
									onChange={(next) => onChange({ ...value, [key]: next ?? "" })}
									disabled={disabled}
								/>
								{error && <p className="text-xs text-rose-600">{error}</p>}
							</div>
							{previewSrc && (
								<div className="flex flex-col items-start gap-2">
									<a href={previewSrc} target="_blank" rel="noreferrer" className="block relative h-40 w-40 overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
										<img
											src={previewSrc}
											alt=""
											className="h-full w-full object-cover"
											style={centerPosition ? { objectPosition: centerPosition } : undefined}
										/>
									</a>
									<a
										href={previewSrc}
										download
										target="_blank"
										rel="noreferrer"
										className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
									>
										Télécharger
									</a>
								</div>
							)}
						</div>
					);
				}

				if (type === "file") {
					return (
						<div key={key} className="space-y-1">
							<FilePickerInput
								label={label}
								value={fieldValue ?? null}
								onChange={(next) => onChange({ ...value, [key]: next ?? null })}
								disabled={disabled}
							/>
							{error && <p className="text-xs text-rose-600">{error}</p>}
						</div>
					);
				}

				if (type === "components") {
					const componentsValue = Array.isArray(fieldValue) ? fieldValue : [];
					return (
						<div key={key} className="space-y-2">
							<EmbeddedComponentsEditor
								label={label}
								helper="Manage reusable content blocks and preview below."
								value={componentsValue}
								onChange={(next) => onChange({ ...value, [key]: next })}
								disabled={disabled}
							/>
							{error && <p className="text-xs text-rose-600">{error}</p>}
						</div>
					);
				}

				if (type === "longtext" || type === "textarea" || type === "richtext") {
					// Long-form text content — renders as a multi-line textarea.
					// Used for fields migrated from Drupal `body[und][0][value]`
					// (HTML strings that can be 50+ lines long).
					const txt = typeof fieldValue === "string" ? fieldValue : (fieldValue == null ? "" : String(fieldValue));
					return (
						<label key={key} className="space-y-1 block">
							<span className="text-xs font-semibold text-slate-500">{label}</span>
							<textarea
								className={`w-full rounded-lg border px-3 py-2 text-sm font-mono focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50 ${
									error ? "border-rose-300" : "border-slate-200"
								}`}
								rows={Math.min(20, Math.max(6, (txt.match(/\n/g)?.length ?? 0) + 3))}
								value={txt}
								onChange={(event) => onChange({ ...value, [key]: event.target.value })}
								disabled={disabled}
							/>
							{error && <p className="text-xs text-rose-600">{error}</p>}
						</label>
					);
				}

				if (type === "select") {
					// Dropdown bound to field.options (defined in the content type config).
					// Each option carries { value, label }; the stored value is the `value`.
					const options = Array.isArray(field.options) ? field.options : [];
					const currentValue = typeof fieldValue === "string" ? fieldValue : (fieldValue == null ? "" : String(fieldValue));
					return (
						<label key={key} className="space-y-1 block">
							<span className="text-xs font-semibold text-slate-500">{label}</span>
							<select
								className={`w-full rounded-lg border px-3 py-2 text-sm focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50 ${
									error ? "border-rose-300" : "border-slate-200"
								}`}
								value={currentValue}
								onChange={(event) => {
									const raw = event.target.value;
									onChange({ ...value, [key]: raw === "" ? null : raw });
								}}
								disabled={disabled}
							>
								<option value="">— Sélectionner —</option>
								{options.map((opt, i) => {
									const optValue = (opt?.value ?? "").toString();
									const optLabel = opt?.label?.trim() || optValue;
									return (
										<option key={`${key}-opt-${i}`} value={optValue}>
											{optLabel}
										</option>
									);
								})}
							</select>
							{error && <p className="text-xs text-rose-600">{error}</p>}
						</label>
					);
				}

				if (type === "list") {
					// Recursive list: each row is itself a content-item form built from
					// field.itemFields. Add / remove a row preserves the order. The data
					// shape stored is an array of { key: value } objects matching
					// itemFields' resolved keys.
					const itemFields = Array.isArray(field.itemFields) ? field.itemFields : [];
					const rows = Array.isArray(fieldValue) ? fieldValue : [];
					const updateRows = (next: any[]) => onChange({ ...value, [key]: next });
					return (
						<div key={key} className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
							<div className="flex items-center justify-between">
								<span className="text-xs font-semibold text-slate-500">{label}</span>
								<button
									type="button"
									onClick={() => updateRows([...rows, {}])}
									disabled={disabled}
									className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
								>
									+ Ajouter
								</button>
							</div>
							{rows.length === 0 ? (
								<p className="text-xs text-slate-500">Aucune entrée. Cliquez sur « Ajouter » pour commencer.</p>
							) : (
								<div className="space-y-3">
									{rows.map((row: any, rowIdx: number) => (
										<div key={`${key}-row-${rowIdx}`} className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
											<div className="flex items-center justify-between">
												<span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">#{rowIdx + 1}</span>
												<button
													type="button"
													onClick={() => updateRows(rows.filter((_: any, i: number) => i !== rowIdx))}
													disabled={disabled}
													className="text-xs font-semibold text-rose-600 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
												>
													Supprimer
												</button>
											</div>
											<ContentItemForm
												fields={itemFields}
												value={row && typeof row === "object" ? row : {}}
												onChange={(nextRow) => {
													const nextRows = rows.slice();
													nextRows[rowIdx] = nextRow;
													updateRows(nextRows);
												}}
												disabled={disabled}
											/>
										</div>
									))}
								</div>
							)}
							{error && <p className="text-xs text-rose-600">{error}</p>}
						</div>
					);
				}

				const inputType = type === "date" ? "text" : type === "number" || type === "int" || type === "float" ? "number" : "text";
				const step = type === "int" ? 1 : type === "float" ? "any" : undefined;
				const displayValue =
					type === "date"
						? toDisplayDate(fieldValue)
						: inputType === "number"
						? fieldValue ?? ""
						: typeof fieldValue === "string"
							? fieldValue
							: fieldValue ?? "";

				return (
					<label key={key} className="space-y-1 block">
						<span className="text-xs font-semibold text-slate-500">{label}</span>
						{type === "date" ? (
							<div className="flex flex-wrap items-center gap-2">
								<input
									type="text"
									placeholder="DD/MM/YYYY"
									inputMode="numeric"
									className={`flex-1 min-w-[200px] rounded-lg border px-3 py-2 text-sm focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50 ${
										error ? "border-rose-300" : "border-slate-200"
									}`}
									value={displayValue}
									onChange={(event) => {
										const raw = event.target.value;
										onChange({ ...value, [key]: normalizeDateInput(raw) });
									}}
									disabled={disabled}
								/>
								<input
									ref={(el) => {
										dateInputRefs.current[key] = el;
									}}
									type="date"
									className="sr-only"
									value={toPickerDate(displayValue)}
									onChange={(event) => {
										onChange({ ...value, [key]: normalizeDateInput(event.target.value) });
									}}
									disabled={disabled}
								/>
								<button
									type="button"
									onClick={() => {
										const input = dateInputRefs.current[key];
										if (!input || disabled) return;
										if (typeof (input as any).showPicker === "function") {
											(input as any).showPicker();
										} else {
											input.click();
										}
									}}
									disabled={disabled}
									className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
								>
									<FiCalendar className="h-3.5 w-3.5" /> Pick
								</button>
								<button
									type="button"
									onClick={() => onChange({ ...value, [key]: buildTodayValue() })}
									disabled={disabled}
									className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
								>
									Today
								</button>
							</div>
						) : (
							<input
								type={inputType}
								step={step}
								placeholder={type === "date" ? "DD/MM/YYYY" : undefined}
								inputMode={type === "date" ? "numeric" : undefined}
								className={`w-full rounded-lg border px-3 py-2 text-sm focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50 ${
									error ? "border-rose-300" : "border-slate-200"
								}`}
								value={displayValue}
								onChange={(event) => {
									const raw = event.target.value;
									const nextValue =
										inputType === "number"
											? raw === "" ? null : Number(raw)
											: type === "date"
												? normalizeDateInput(raw)
												: raw;
									onChange({ ...value, [key]: nextValue });
								}}
								disabled={disabled}
							/>
						)}
						{error && <p className="text-xs text-rose-600">{error}</p>}
					</label>
				);
			})}
		</div>
	);
};

export default ContentItemForm;
